import {
    CloseButton,
    CrudMode,
    Stack,
    Switch,
    Typography,
    UpdateButton,
    newToast,
    ValidationErrors,
    DropDownMenu,
    MenuItemType,
} from "@compass/core";

import { SubmitButton } from "components";
import { Permission, PermissionAction, PostHighSchoolStatus } from "generated/openApi";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import { ListContext } from "@compass/core";
import ApiService from "services/apiService";

export const PostHSPlanStatusPageHeader = (props: {
    rootPermission: Permission;
    handle: string;
    crudMode: CrudMode;
    fetch: (id: number) => void;
    reroute: (id: number, crudMode: CrudMode) => void;
    postHSPlanStatus: PostHighSchoolStatus;
    setPostHSPlanStatus: (postHSPlanStatus: PostHighSchoolStatus) => void;
    initPostHSPlanStatus: PostHighSchoolStatus;
    setValidationErrors: (errors: ValidationErrors) => void;
    unsavedChanges: boolean;
    resetUnsavedChanges: (items: any[]) => void;
    closeOnSave?: boolean | undefined;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const id = props.postHSPlanStatus.id;
    const [createAnother, setCreateAnother] = useState<boolean>(false);
    const { loadList } = useContext(ListContext);

    const save = (postHSPlanStatus: PostHighSchoolStatus) => {
        props.setValidationErrors({});

        if (id > 0) {
            ApiService.postHSPlanStatus
                .saveUpdate(postHSPlanStatus)
                .then(() => {
                    loadList?.({ resetPage: true });
                    props.fetch(id);
                    dispatch(setAppToast(newToast("success")));
                    if (props.closeOnSave) navigate("../");
                    else props.reroute(id, "view");
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.response);
                });
        } else {
            ApiService.postHSPlanStatus
                .saveCreate(postHSPlanStatus)
                .then((response) => {
                    loadList?.({ resetPage: true });
                    const newId = response;

                    if (createAnother) {
                        props.setPostHSPlanStatus(props.initPostHSPlanStatus);
                        props.resetUnsavedChanges([props.initPostHSPlanStatus]);
                    } else {
                        props.fetch(newId);
                        if (props.closeOnSave) navigate("../");
                        else props.reroute(newId, "view");
                    }

                    dispatch(setAppToast(newToast("success")));
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.response);
                });
        }
    };

    const postDelete = (id: number) => {
        if (window.confirm("Are you sure you want to delete this PostHSPlanStatus?")) {
            ApiService.postHSPlanStatus.delete(id).then(() => {
                loadList?.({ resetPage: true });
                navigate("../");
                dispatch(setAppToast(newToast("success")));
            });
        }
    };

    const menuItems: MenuItemType[] = [];
    if (id > 0 && isAuthorized(props.rootPermission, PermissionAction.Delete)) {
        menuItems.push({
            label: <Typography color="error">Delete</Typography>,
            action: () => postDelete(id),
        });
    }

    return (
        <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing="sm">
                <Typography variant="inherit">
                    {id === 0 ? "New" : ""} {props.handle}
                </Typography>
                {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
                {id === 0 && (
                    <Switch
                        value={createAnother}
                        onChange={(e) => setCreateAnother(e.target.checked)}
                        label="+ more"
                    />
                )}

                <CloseButton theme="Dark" confirm={props.unsavedChanges} />

                {props.crudMode === "view" &&
                    isAuthorized(props.rootPermission, PermissionAction.Update) && (
                        <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
                    )}

                {props.crudMode === "edit" && (
                    <SubmitButton
                        theme="Dark"
                        onClick={() => save(props.postHSPlanStatus)}
                        disabled={!props.unsavedChanges}
                    />
                )}
            </Stack>
        </Stack>
    );
};